import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { X } from 'lucide-react';
import { toast } from 'sonner';

const EditCustomerModal = ({ customer, onClose, onCustomerUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    alternate_mobile: '',
    email: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        mobile: customer.mobile || '',
        alternate_mobile: customer.alternate_mobile || '',
        email: customer.email || '',
        address: customer.address || ''
      });
    }
  }, [customer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate mobile numbers
    if (formData.mobile.length !== 10) {
      toast.error('Primary mobile number must be exactly 10 digits');
      return;
    }
    
    if (formData.alternate_mobile && formData.alternate_mobile.length !== 10) {
      toast.error('Alternate mobile number must be exactly 10 digits');
      return;
    }
    
    setLoading(true);

    try {
      const customerData = {
        ...formData,
        id: customer.id
      };
      
      const res = await axiosInstance.put(`/customers/${customer.id}`, customerData);
      toast.success('Customer updated successfully!');
      onCustomerUpdated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error updating customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-blue-100 to-indigo-100 p-6 flex justify-between items-center border-b-2 border-blue-200">
          <h2 className="text-xl font-bold text-gray-800">Edit Customer Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Customer Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="input-field w-full"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="Enter customer name"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Primary Mobile Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              inputMode="numeric"
              className="input-field w-full"
              value={formData.mobile}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                setFormData({ ...formData, mobile: value });
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pastedText = e.clipboardData.getData('text');
                const value = pastedText.replace(/\D/g, '').slice(0, 10);
                setFormData({ ...formData, mobile: value });
              }}
              required
              minLength={10}
              maxLength={10}
              pattern="[0-9]{10}"
              placeholder="10-digit mobile number"
            />
            {formData.mobile && formData.mobile.length !== 10 && (
              <p className="text-xs text-red-500 mt-1">Must be exactly 10 digits ({formData.mobile.length}/10)</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Alternate Mobile Number (Optional)
            </label>
            <input
              type="tel"
              inputMode="numeric"
              className="input-field w-full"
              value={formData.alternate_mobile}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                setFormData({ ...formData, alternate_mobile: value });
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pastedText = e.clipboardData.getData('text');
                const value = pastedText.replace(/\D/g, '').slice(0, 10);
                setFormData({ ...formData, alternate_mobile: value });
              }}
              minLength={10}
              maxLength={10}
              pattern="[0-9]{10}"
              placeholder="10-digit mobile number (optional)"
            />
            {formData.alternate_mobile && formData.alternate_mobile.length > 0 && formData.alternate_mobile.length !== 10 && (
              <p className="text-xs text-red-500 mt-1">Must be exactly 10 digits ({formData.alternate_mobile.length}/10)</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Email (Optional)
            </label>
            <input
              type="email"
              className="input-field w-full"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="customer@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Address (Optional)
            </label>
            <textarea
              className="input-field w-full"
              rows={3}
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Enter customer address"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-600 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCustomerModal;
